import React from 'react';
import renderHTML from 'react-render-html';
import Select from 'react-select';
import { goToLine } from '../../../services/editor-manipulation';

class AmendmentSection extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            shown: [],
            showAll: false,
            showInactiveAmendments: false,
            viableVersion: null,
            selectOptions: props.versions?.map(t => ({ label: t.DocumentCode, value: t.DocumentCode })) || [],
        }

        this.getAmendmentDescription = this.getAmendmentDescription.bind(this);
        this.showAmendment = this.showAmendment.bind(this);
        this.showAllAmendments = this.showAllAmendments.bind(this);
        this.toggleInactiveAmendments = this.toggleInactiveAmendments.bind(this);
        this.copyToClipboard = this.copyToClipboard.bind(this);
        this.docRef = React.createRef();
        this.holderRef = React.createRef();
    }

    getAmendmentDescription(amendment) {
        let descriptionAndDisposition = amendment.Description;

        if (!amendment.DocumentCode || amendment.DocumentCode === "") {
            return descriptionAndDisposition;
        }

        if (amendment.TextDispositionID === 12) {
            return descriptionAndDisposition + " Engrossed"
        }

        if (amendment.TextDisposition === "Hold") {
            return descriptionAndDisposition;
        } else if (amendment.TextDisposition !== "Posted") {
            descriptionAndDisposition = `${amendment.Description} ${amendment.TextDisposition || ''}`;
        }

        if (amendment.IsReprint && !descriptionAndDisposition.includes("(Reprint)")) {
            descriptionAndDisposition = descriptionAndDisposition + " (Reprint)";
        }

        if (amendment.LDNumber) {
            descriptionAndDisposition = descriptionAndDisposition + " - " + amendment.LDNumber;
        }

        return descriptionAndDisposition;
    }

    showAmendment(e, index) {
        if (e) {
            e.stopPropagation();
        }
        let shown = [...this.state.shown];
        shown[index] = !shown[index];
        this.setState({
            shown: shown
        });
    }

    showAllAmendments(e) {
        if (e) {
            e.stopPropagation();
        }
        const opposite = !this.state.showAll;
        let shown = new Array(this.props.amendments.length).fill(opposite);
        this.setState({
            showAll: opposite,
            shown: shown
        });
    }

    toggleInactiveAmendments() {
        this.setState(state => ({
            showInactiveAmendments: !state.showInactiveAmendments
        }));
    }

    componentDidMount() {
        if (this.props.inPortal) {
            const style = document.createElement('style');
            style.innerHTML = '[aria-live] {display: none;}';
            this.holderRef.current?.ownerDocument?.head?.appendChild(style)
            const viableVersion = this.props.versions
                .find(t => t.IsActive && t.IsPublic)
            this.setState({ viableVersion });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // Did not realize the portal would also be updated when navigating to a new bill.
        if (this.props.amendments !== prevProps.amendments) {
            this.setState({
                shown: [],
                showAll: false,
                showInactiveAmendments: false,
                viableVersion: this.props.inPortal ? this.props.versions
                    .find(t => t.IsActive && t.IsPublic) : null,
                selectOptions: this.props.versions?.map(t => ({ label: t.DocumentCode, value: t.DocumentCode })) || []
            });
        }
        // Because the amendments are rendered as HTML, we have to suffer
        if (JSON.stringify(this.state.shown) !== JSON.stringify(prevState.shown) && this.state.shown.filter(s => s).length > 0) {
            Array.from(document.getElementsByClassName('button copy')).forEach(el => el.remove());
            Array.from(document.getElementsByClassName('button view')).forEach(el => el.remove());
            const amendmentItems = this.holderRef.current?.getElementsByClassName('amendment-entry');
            this.state.shown.forEach((s, index) => {
                if (s) {
                    const amendment = this.props.amendments[index];
                    // Otherwise blows up with inactive amendments.
                    const items = amendmentItems[index]?.getElementsByClassName('textbi') ?? [];
                    Array.from(items).forEach((item, j) => {
                        const button = document.createElement('button');
                        button.textContent = ' ';
                        button.className = 'button view';
                        button.onclick = () => { goToLine(amendment.AmendmentItems[j].LineNumber) };
                        item.parentElement.insertBefore(button, item.nextSibling);
                        button.style.position = 'absolute';
                        button.style.left = '25px'
                        button.style.marginTop = '-25px'
                        const insertions = amendment.AmendmentItems[j]?.amendmentChanges?.flatMap(c => c.AmendmentActions.filter(a => a.action === 'insert')) ?? [];
                        insertions.forEach(ins => {
                            // Have to check the position cause dupes.
                            // Links have different quotes!!!
                            const foundInsert = Array.from(this.holderRef.current.getElementsByClassName(ins.TextClassName)).find(el => el.innerHTML.replaceAll('"', "'") === ins.text.replaceAll('"', "'") && item.compareDocumentPosition(el) === Node.DOCUMENT_POSITION_FOLLOWING)
                            if (foundInsert) {
                                const button = document.createElement('button');
                                button.textContent = ' ';
                                button.className = 'button copy';
                                button.onclick = () => { this.copyToClipboard(ins) };
                                foundInsert.parentElement.insertBefore(button, foundInsert.nextSibling);
                                button.style.position = 'absolute';
                                button.style.left = '25px'
                                button.style.marginTop = '-25px'
                            }
                        })
                    });
                }
            });
        }
        if (this.state.viableVersion !== prevState.viableVersion) {
            fetch(this.state.viableVersion.PDFFile[0].FileURL).then(r => r.blob()).then(blob => {
                const url = URL.createObjectURL(blob);
                this.docRef.current.src = url;
            }).catch(e => {
                console.error("Error fetching viable version:", e);
            })
        }
    }

    copyToClipboard(amendment) {
        const html = amendment.text;
        const blob = new Blob([html], { type: 'text/html' });
        const justTheText = amendment.text.replace(/(\r)?\n/g, ' ').replace(/<!--.+-->/g, '').replace(/<.*?>/g, ''); //ugh. I got this from BDS, should work ok.
        const clipboardItem = new ClipboardItem({ 'text/html': blob, 'text/plain': justTheText });
        this.holderRef.current?.ownerDocument?.defaultView?.navigator?.clipboard.write([clipboardItem])
            .then(() => {
                this.props.copySuccessful();
            })
            .catch(err => {
                alert('Failed to copy HTML: ' + err);
            });
    }

    render() {
        const { shown, showAll, showInactiveAmendments } = this.state;
        let amendments = [...this.props.amendments];
        if (!showInactiveAmendments) {
            amendments = amendments.filter(amendment => amendment.IsActive);
        }

        const panelStyle = this.props.inPortal ? { display: 'grid', gridTemplateColumns: '1fr 1fr', height: '100%' } : {}
        const customStyles = {
            option: (base, state) => ({
                ...base,
                fontSize: '14px',
                fontWeight: state.data.IsActive ? 'bold' : 'normal'
            }),
            control: (base) => ({
                ...base,
                fontSize: '14px',
                background: '#f1f1f1',
                border: '1px solid #d8d8d8',
                borderRadius: '0 0 5px 5px'
            }),
            menu: (base) => ({
                ...base,
                width: '245px',
                marginTop: '1px',

            }),
            menuList: (base) => ({
                ...base,
                marginTop: '1px'
            }),
            indicatorSeparator: (base) => ({
                ...base,
                display: "none"
            }),
            singleValue: (base, state) => {
                return { ...base, };
            }
        }

        return (
            <div style={panelStyle} ref={this.holderRef}>
                <div style={{ backgroundColor: 'white', padding: "10px" }}>
                    <div className="flex-row">
                        <div className="flex-row flex-vertical-center">
                            {this.props.amendments.find(amendment => !amendment.IsActive) &&
                                <React.Fragment>
                                    <input style={{ margin: '0px 5px' }} type="checkbox" onChange={this.toggleInactiveAmendments} checked={showInactiveAmendments} />
                                    <label>Show Inactive Amendments</label>
                                </React.Fragment>
                            }
                        </div>
                        <div>
                            {this.props.amendments.length > 0 &&
                                <button onClick={this.showAllAmendments} className="button-link" type="button">{showAll ? "Collapse All +/-" : "Expand All +/-"}</button>
                            }
                        </div>
                    </div>
                    {amendments.map((amendment, index) =>
                        <div key={index} className='amendment-entry'>
                            <div>
                                <button onClick={e => this.showAmendment(e, index)} className={`data-url ${shown[index] ? "arrow-up" : "arrow-down"}`}></button>
                                <label className="small-text">{this.getAmendmentDescription(amendment)}</label>
                                {!amendment.IsActive && <label>{" (INACTIVE)"}</label>}
                            </div>
                            {shown[index] &&
                                <div className="dlas-forms amendments-review">
                                    {amendment.DraftText ? renderHTML(amendment.DraftText) : ''}
                                </div>
                            }
                            {index !== amendments.length - 1 && <hr />}
                        </div>
                    )}
                </div>
                {this.props.inPortal && <div>
                    <Select
                        isSearchable={false}
                        options={this.state.selectOptions}
                        maxMenuHeight={170}
                        value={this.state.selectOptions.find(t => t.value === this.state.viableVersion?.DocumentCode)}
                        onChange={(v) => this.setState({ viableVersion: this.props.versions.find(t => t.DocumentCode === v.value) })}
                        styles={customStyles}
                        classNamePrefix='no-width-dropdown-input'
                    />
                    <iframe style={{ height: '100%', width: '100%' }} ref={this.docRef} />
                </div>}
            </div>
        );
    }
}

export default AmendmentSection;
